import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";

export default function AddCostPriceModal({
  isOpen,
  onClose,
  onSave,
  editingPrice,
  categories,
}) {
  const [form, setForm] = useState({
    productName: "",
    supplier: "",
    categoryId: "",
    cost: "",
    shipping: "",
    status: "Active",
    isActive: true,
  });

  useEffect(() => {
    if (editingPrice) {
      setForm({
        productName: editingPrice.productName || "",
        supplier: editingPrice.supplier || "",
        categoryId: String(editingPrice.categoryId || editingPrice.category?.id || ""),
        cost: String(editingPrice.cost ?? ""),
        shipping: String(editingPrice.shipping ?? ""),
        status: editingPrice.status || "Active",
        isActive: editingPrice.isActive ?? true,
      });
    } else {
      setForm({
        productName: "",
        supplier: "",
        categoryId: categories?.[0]?.id ? String(categories[0].id) : "",
        cost: "",
        shipping: "",
        status: "Active",
        isActive: true,
      });
    }
  }, [editingPrice, isOpen, categories]);

  if (!isOpen) return null;

  const total = Number(form.cost || 0) + Number(form.shipping || 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      productName: form.productName.trim(),
      supplier: form.supplier.trim(),
      categoryId: Number(form.categoryId),
      cost: Number(form.cost || 0),
      shipping: Number(form.shipping || 0),
      status: form.status,
      isActive: form.isActive,
    });
  };

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";

  const labelClass = "mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-slate-800">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {editingPrice ? "Edit Cost Price" : "Add Cost Price"}
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Enter product and pricing details below.
            </p>
          </div>

          <button
            onClick={onClose}
            className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            type="button"
            aria-label="Close modal"
          >
            <FiX className="text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className={labelClass}>Product Name *</label>
              <input
                type="text"
                value={form.productName}
                onChange={(e) => setForm({ ...form, productName: e.target.value })}
                className={inputClass}
                placeholder="e.g. Mobile Phone X"
                required
              />
            </div>

            <div>
              <label className={labelClass}>Supplier *</label>
              <input
                type="text"
                value={form.supplier}
                onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                className={inputClass}
                placeholder="e.g. KG Electronics"
                required
              />
            </div>

            <div>
              <label className={labelClass}>Category *</label>
              <select
                value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                className={inputClass}
                required
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className={inputClass}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>Cost (£) *</label>
              <input
                type="number"
                step="0.01"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                className={inputClass}
                placeholder="0.00"
                required
              />
            </div>

            <div>
              <label className={labelClass}>Shipping (£)</label>
              <input
                type="number"
                step="0.01"
                value={form.shipping}
                onChange={(e) => setForm({ ...form, shipping: e.target.value })}
                className={inputClass}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300">
            Total Preview: <span className="font-semibold text-slate-900 dark:text-slate-100">£{total.toFixed(2)}</span>
          </div>

          <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600"
            />
            Active
          </label>

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              {editingPrice ? "Update Price" : "Save Price"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}